import {
  aggregateByLine,
  aggregatePeriods,
  sumPeriods,
  summarizeWindow,
  type Basis,
  type CashFlowEntry as CoreEntry,
  type DayRollup as CoreDay,
} from '@progear/finance-core';
import { formatEur, type CashFlowEntry, type DayRollup, type MoneyMode } from '@/lib/cashFlow';

/**
 * The company-level KPI strip of the primary view (module 1.1) — müük / arveldatud
 * / arveldamata / prognoos · tegelik kulu / marginaal · saldo. Every figure comes
 * from finance-core's `summarizeWindow` over the visible window (L9 — the component
 * never sums money itself). The strip follows the shared NET/GROSS display toggle
 * (L14 — the same lens capability as the grid/matrix) and marks every figure with
 * an N (neto) / B (bruto) letter; the saldo is real cash on the account, so it
 * stays GROSS (always B) regardless of the toggle.
 */
export interface KpiStripProps {
  days: DayRollup[];
  entries: CashFlowEntry[];
  today: string;
  /** The shared reporting basis — under accrual every line is placed on its
   *  document date and the (cash-only) closing balance KPI is hidden. */
  basis?: Basis;
  /** The shared NET/GROSS display toggle — picks which precomputed side of every
   *  P&L figure is shown (never a recalculation, L9). Defaults to gross. */
  mode?: MoneyMode;
}

function Kpi({
  label,
  value,
  marker,
  note,
  tone,
  size = 'sm',
}: {
  label: string;
  value: string;
  /** N (neto) / B (bruto) — which side of the money the figure shows. */
  marker: 'N' | 'B';
  note?: string;
  tone?: 'income' | 'cost' | 'neutral';
  size?: 'sm' | 'lg';
}) {
  const color =
    tone === 'income'
      ? 'text-[hsl(var(--income))]'
      : tone === 'cost'
        ? 'text-[hsl(var(--cost))]'
        : 'text-foreground';
  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex items-baseline gap-1">
        <span className={`tabular-nums font-semibold ${size === 'lg' ? 'text-base leading-none' : 'text-xs'} ${color}`}>{value}</span>
        <span
          aria-label={marker === 'N' ? 'neto' : 'bruto'}
          className="text-[8px] font-semibold leading-none text-[hsl(var(--vatmode))]"
        >
          {marker}
        </span>
        {note && <span className="text-[9px] text-muted-foreground">{note}</span>}
      </div>
    </div>
  );
}

/** Thin vertical rule grouping the KPI stats into income · result · balance. */
function KpiDivider() {
  return <span aria-hidden="true" className="h-7 w-px shrink-0 self-center bg-border" />;
}

export function KpiStrip({ days, entries, today, basis = 'cashBasis', mode = 'gross' }: KpiStripProps) {
  if (days.length === 0) return null;
  const from = days[0].date;
  const to = days[days.length - 1].date;
  const periods = aggregatePeriods(days as CoreDay[], 'month');
  const closingBalance = sumPeriods(periods).closingBalance;
  const lines = aggregateByLine(entries as CoreEntry[], { today, from, to, basis });
  const k = summarizeWindow(lines, closingBalance, mode);
  // The saldo is real cash on the account — a cash truth; under the accrual
  // basis it cannot reconcile with document-dated sums, so it is hidden.
  const accrual = basis === 'accrualBasis';
  // N (neto) / B (bruto) — every P&L figure follows the shared display toggle.
  const marker = mode === 'net' ? 'N' : 'B';

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-border bg-card/60 px-3 py-2" data-testid="kpi-strip">
      <Kpi label="Müük" value={formatEur(k.sales)} marker={marker} note="alus · pakkumine" tone="income" size="lg" />
      <KpiDivider />
      <Kpi label="Arveldatud" value={formatEur(k.invoiced)} marker={marker} note="kindel + kokkulepitud" />
      <Kpi label="Arveldamata" value={formatEur(k.uninvoiced)} marker={marker} note="prognoos" />
      <Kpi label="Prognoos" value={formatEur(k.forecast)} marker={marker} />
      <KpiDivider />
      <Kpi label="Tegelik kulu" value={formatEur(k.actualCost)} marker={marker} tone="cost" />
      <Kpi label="Marginaal" value={formatEur(k.margin)} marker={marker} note={`${k.marginPct > 0 ? '+' : ''}${k.marginPct}%`} tone="income" size="lg" />
      {!accrual && (
        <>
          <KpiDivider />
          {/* Real cash on the account — always gross, whatever the toggle says. */}
          <Kpi label="Saldo (lõpp)" value={formatEur(k.closingBalance)} marker="B" tone="neutral" size="lg" />
        </>
      )}
    </div>
  );
}
